/**
 * Settings → Bot → AI engine: answer chat with the Emergent cloud key (budgeted,
 * costs credits) or a local Ollama server on the VPS (free). URL + model fields,
 * a Test connection that probes without saving, and the install commands.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Cloud, Loader2, Server, Shuffle, XCircle } from "lucide-react";
import api from "@/lib/api";

const INSTALL = `# on the VPS (Ubuntu/Debian), as root or with sudo:
curl -fsSL https://ollama.com/install.sh | sh
ollama pull llama3.1:8b        # ~5 GB · the default, fastest on CPU
ollama pull qwen2.5:14b        # ~9 GB · sharper answers, about 2x slower (optional)
# let the app's Docker container reach it, then keep it running after reboots:
mkdir -p /etc/systemd/system/ollama.service.d
printf '[Service]\nEnvironment="OLLAMA_HOST=0.0.0.0:11434"\n' > /etc/systemd/system/ollama.service.d/override.conf
systemctl daemon-reload && systemctl enable --now ollama && systemctl restart ollama
ufw allow from 172.16.0.0/12 to any port 11434 2>/dev/null || true   # Docker networks only
curl http://127.0.0.1:11434/api/tags   # should list the model
# then redeploy the app once (./deploy/redeploy.sh …) so the container gets the host alias`;

// Preset choices for the Model dropdown. Default stays llama3.1:8b (DEFAULT_ENGINE in backend/elitebot.py).
const MODELS = [
  { id: "llama3.1:8b", label: "Llama 3.1 8B — fast (default)", hint: "~5 GB RAM · CPU: ~15–25 s per chat answer, ~20–40 s per scanned receipt" },
  { id: "qwen2.5:14b", label: "Qwen 2.5 14B — sharper, ~2x slower", hint: "~9 GB RAM · CPU: ~30–60 s per chat answer, ~40–90 s per scanned receipt · better at reading messy invoices" },
];

export default function BotEnginePanel() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ engine: "emergent", ollama_url: "", ollama_model: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [probe, setProbe] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sample, setSample] = useState("");
  const [route, setRoute] = useState(null);

  useEffect(() => {
    api.get("/ai/bot-engine").then((r) => { setCfg(r.data); setForm(r.data); }).catch(() => {});
    api.get("/ai/bot-engine/usage").then((r) => setUsage(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!sample.trim()) { setRoute(null); return; }
    const t = setTimeout(() => api.get("/ai/bot-engine/route", { params: { q: sample } }).then((r) => setRoute(r.data)).catch(() => {}), 350);
    return () => clearTimeout(t);
  }, [sample]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/ai/bot-engine", form);
      setCfg(r.data); setForm(r.data);
      toast.success((r.data.scan_local ? "Document scans now parse locally first. " : "") + (r.data.engine === "ollama" ? `Chat now answers with local Ollama (${r.data.ollama_model}) — no credits used`
        : r.data.engine === "smart" ? `Smart routing on — payroll & money questions go to the cloud, everything else to ${r.data.ollama_model}`
        : "Chat now answers with the Emergent cloud model"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const test = async () => {
    setTesting(true); setProbe(null);
    try { setProbe((await api.post("/ai/bot-engine/test", { ...form, engine: "ollama" })).data); }
    catch (e) { setProbe({ ok: false, detail: e?.response?.data?.detail || "Test failed" }); }
    finally { setTesting(false); }
  };

  if (!cfg) return null;
  const dirty = JSON.stringify(form) !== JSON.stringify(cfg);
  const preset = MODELS.find((m) => m.id === form.ollama_model);
  const isPreset = !!preset;
  // "llama3.1:8b" installed as "llama3.1:8b" or listed with a tag suffix — compare on the bare name
  const installed = probe?.models ? probe.models.map((m) => m.split(":latest")[0]) : null;
  const inp = "w-full rounded-md border border-white/15 bg-white/5 px-3 py-1.5 text-sm text-white focus:border-[#4f6fe8] focus:outline-none";
  const Opt = ({ value, icon: Icon, title, sub, testId }) => (
    <button type="button" onClick={() => setForm({ ...form, engine: value })} data-testid={testId} aria-pressed={form.engine === value}
      className={`flex flex-1 items-start gap-3 rounded-lg border p-3 text-left transition-colors ${form.engine === value ? "border-[#1a3fd1] bg-[#1a3fd1]/15" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${form.engine === value ? "text-[#8fa8ff]" : "text-white/50"}`} />
      <span><span className="block text-sm font-semibold text-white">{title}</span><span className="block text-xs text-white/60">{sub}</span></span>
    </button>
  );

  const Toggle = ({ k, testId, title, sub }) => (
    <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={testId}>
      <button type="button" role="switch" aria-checked={!!form[k]} onClick={() => setForm({ ...form, [k]: !form[k] })} data-testid={`${testId}-toggle`}
        className={`mt-0.5 flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${form[k] ? "justify-end bg-[#1a3fd1]" : "justify-start bg-white/15"}`}>
        <span className="h-4 w-4 rounded-full bg-white shadow" />
      </button>
      <span><span className="block text-sm font-semibold text-white">{title}</span>
        <span className="block text-xs text-white/60">{sub}</span></span>
    </label>
  );

  return (
    <section className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="settings-bot-engine">
      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">AI use · local vs Emergent credits</p>
      <p className="mb-3 text-xs text-white/60">Choose what answers chat and whether document scans run on the free local model. Alerts, digests and discrepancy checks are plain code and never cost anything.</p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Opt value="emergent" icon={Cloud} title="Emergent (cloud)" sub="Best answers · uses credits · per-person monthly budget applies" testId="bot-engine-emergent" />
        <Opt value="ollama" icon={Server} title="Local (Ollama)" sub="Free · runs on your VPS · slower on CPU, no budget limit" testId="bot-engine-ollama" />
        <Opt value="smart" icon={Shuffle} title="Smart routing" sub="Payroll & money questions → cloud · everything else → local" testId="bot-engine-smart" />
      </div>

      {/* one switch for everything that costs credits besides chat: Crossroads / Hilti / Overhead
          scans, auto scans, PDF imports, QB categorising — all parsed on the local model first */}
      <Toggle k="scan_local" testId="bot-engine-scan-local" title="Parse all document scans locally"
        sub="Crossroads, Hilti and Overhead email scans, nightly auto scans, PDF imports and QuickBooks categorising use the local model instead of credits. Slower, and anything the local model can't read cleanly still falls back to the cloud." />
      <Toggle k="ocr_cheap" testId="bot-engine-ocr-cheap" title="Estimating: read drawing pages with Gemini Flash"
        sub="The per-page OCR pass (one vision call per sheet) runs on the cheap Gemini Flash model; Claude still writes the estimate. Pages are also cached by content, so re-running an estimate never re-reads a sheet it has already seen." />
      <Toggle k="review_local" testId="bot-engine-review-local" title="Estimating: free second opinion on the local model"
        sub="After each estimate finishes, the local model reads the line items and flags what looks missing, double-counted or off. Advisory only, runs in the background, costs nothing." />

      {(form.engine === "ollama" || form.engine === "smart" || form.scan_local || form.review_local) && (
        <div className="mt-3 space-y-3" data-testid="bot-engine-ollama-fields">
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block"><span className="mb-1 block font-mono text-[9px] uppercase tracking-widest text-white/50">Ollama URL</span>
              <input value={form.ollama_url || ""} onChange={(e) => setForm({ ...form, ollama_url: e.target.value })} placeholder="http://host.docker.internal:11434" className={inp} data-testid="bot-engine-url" /></label>
            <label className="block"><span className="mb-1 block font-mono text-[9px] uppercase tracking-widest text-white/50">Model</span>
              <select value={isPreset ? form.ollama_model : "custom"} onChange={(e) => setForm({ ...form, ollama_model: e.target.value === "custom" ? "" : e.target.value })} className={inp} data-testid="bot-engine-model">
                {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                <option value="custom">Other (type a model name)</option>
              </select></label>
          </div>
          {!isPreset && (
            <input value={form.ollama_model || ""} onChange={(e) => setForm({ ...form, ollama_model: e.target.value })} placeholder="e.g. mistral:7b — must already be pulled on the VPS" className={inp} data-testid="bot-engine-model-custom" />
          )}
          {preset && (
            <p className="font-mono text-[10px] text-white/50" data-testid="bot-engine-model-hint">
              {preset.hint}{installed && !installed.includes(preset.id) && <span className="text-amber-300"> · not installed yet — run <code>ollama pull {preset.id}</code> on the VPS</span>}
              {installed && installed.includes(preset.id) && <span className="text-emerald-300"> · installed</span>}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={test} disabled={testing} data-testid="bot-engine-test"
              className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white/80 hover:border-[#1a3fd1] hover:text-white disabled:opacity-40">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Server className="h-3.5 w-3.5" />} Test connection
            </button>
            {probe && (
              <span className={`inline-flex items-center gap-1.5 text-xs ${probe.ok ? "text-emerald-300" : "text-red-300"}`} data-testid="bot-engine-probe">
                {probe.ok ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />} {probe.detail}
              </span>
            )}
          </div>
          {probe?.models?.length > 0 && <p className="font-mono text-[10px] text-white/50">Models on that server: {probe.models.join(", ")}</p>}
          <details className="rounded-md border border-white/10 bg-black/30 p-3">
            <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-widest text-white/60">Install Ollama on the VPS</summary>
            <pre className="mt-2 overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-emerald-200" data-testid="bot-engine-install">{INSTALL}</pre>
            <p className="mt-2 text-[11px] text-white/50">The app runs in Docker, so <code>host.docker.internal</code> means "this VPS" — the default URL just works once Ollama listens on 0.0.0.0. Pick the model above; it must already be pulled on the VPS (Test connection lists what's installed).</p>
          </details>
        </div>
      )}

      {cfg.engine === "smart" && (
        <div className="mt-3 rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid="bot-engine-route-preview">
          <p className="mb-1 font-mono text-[9px] uppercase tracking-widest text-white/50">Try a question — where would it go?</p>
          <input value={sample} onChange={(e) => setSample(e.target.value)} placeholder="e.g. how much vacation pay do I have banked?" className={inp} data-testid="bot-engine-route-input" />
          {route && <p className={`mt-1 font-mono text-[10px] ${route.route === "cloud" ? "text-[#8fa8ff]" : "text-emerald-300"}`} data-testid="bot-engine-route-result">
            → {route.route === "cloud" ? "Cloud model (money / payroll question)" : "Local Ollama (general question)"}</p>}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={save} disabled={saving || !dirty} data-testid="bot-engine-save"
          className="rounded-md bg-[#1a3fd1] px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white disabled:opacity-40">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save engine"}
        </button>
        <span className="font-mono text-[10px] text-white/50" data-testid="bot-engine-current">Currently: {cfg.engine === "ollama" ? `Local · ${cfg.ollama_model}` : cfg.engine === "smart" ? `Smart · cloud + ${cfg.ollama_model}` : "Emergent cloud"}</span>
      </div>

      {usage && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="bot-engine-usage">
          {[["Cloud answers", usage.cloud_calls, "this month", "text-[#8fa8ff]", "cloud"],
            ["Local answers", usage.local_calls, "free · Ollama", "text-emerald-300", "local"],
            ["Credits spent", `$${usage.cloud_cost.toFixed(2)}`, "cloud model", "text-white", "spent"],
            ["Credits saved", `$${usage.saved.toFixed(2)}`, "answered locally instead", "text-emerald-300", "saved"],
            ["Sheets cached", usage.ocr_pages_cached ?? 0, "drawing pages read once", "text-white", "ocr-cached"],
            ["Sheets reused", usage.ocr_pages_reused ?? 0, "OCR calls not paid for", "text-emerald-300", "ocr-reused"]].map(([lb, v, sub, cls, k]) => (
            <div key={k} className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={`bot-engine-usage-${k}`}>
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50">{lb}</p>
              <p className={`mt-1 font-mono text-lg font-bold tabular-nums ${cls}`}>{v}</p>
              <p className="font-mono text-[10px] text-white/50">{sub}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
